import * as z from "zod";
import {
  assertSsoRouteProviders,
  lockSsoRouteProviders,
} from "../ssoRouteDependencies";
import { zql } from "../generatedDrizzleSchema";
import { commitStepUsages } from "../liveConnectorUsage";
import { assertSuppliersAvailableToDependent } from "../rbac/crossResourceAvailability";
import { uniqueWorkflowName } from "../workflowName";
import { assertCanChangeLiveness } from "./livenessGate";
import { mutator } from "./mutator";

export const recoverWorkflow = mutator({
  schema: z.object({ workflowId: z.string() }),
  resolve: {
    workflow: { kind: "deletedWorkflow", from: "workflowId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
  ],
  fn: async ({ tx, ctx, args: { workflow } }) => {
    await lockSsoRouteProviders(tx, ctx.tenantId, "shared");
    await assertCanChangeLiveness(tx, ctx, workflow);
    // A deleted workflow is excluded from the space's usage edges, so recovery
    // is where the connectors its branch heads run are re-checked.
    const branches = await tx.run(
      zql.workflowBranches
        .where("workflowId", workflow.id)
        .related("headCommit", (c) =>
          c.related("appUsages", (u) =>
            commitStepUsages(u).related("connectedApp")
          )
        )
    );
    const main = branches.find((b) => b.name === "main");
    if (main?.headCommitId)
      await assertSsoRouteProviders(tx, ctx.tenantId, main.headCommitId);
    const suppliers = new Map<string, string>();
    for (const branch of branches)
      for (const usage of branch.headCommit?.appUsages ?? [])
        if (usage.connectedApp)
          suppliers.set(usage.connectedAppId, usage.connectedApp.name);
    await assertSuppliersAvailableToDependent(
      tx,
      workflow.tenantId,
      "space_uses_connected_apps",
      workflow.spaceId,
      [...suppliers].map(([id, label]) => ({ id, label })),
      "recover workflow"
    );

    // A workflow with this name may have been created while this one was
    // deleted, so de-duplicate against the live workflows before restoring it.
    const active = await tx.run(
      zql.workflows
        .where("spaceId", workflow.spaceId)
        .where("deletedAt", "IS", null)
    );
    const name = uniqueWorkflowName(
      workflow.name,
      active.map((w) => w.name)
    );

    await tx.mutate.workflows.update({
      id: workflow.id,
      deletedAt: null,
      name,
    });
  },
});
